import type { FaceBox, MemberState } from '../api/endpoints.js';

/**
 * The pure half of the family-member screen.
 *
 * Everything here is arithmetic or copy over a payload the server already
 * decided, so it is testable without a DOM — and the two things worth testing
 * are here for a reason. The crop can silently show the WRONG PERSON'S FACE,
 * and the presigned URL can silently render dead. Neither failure looks like a
 * failure on screen.
 *
 * WHAT IS DELIBERATELY NOT HERE: any re-derivation of `photos_visible`, of a
 * score band, or of a recommendation target. All three are server-owned, and
 * the screen reads them rather than computing them.
 */

/** Just the roster fields this module needs, so it is callable with a member
 *  row, a member-state row, or a stub in a test. */
export interface RosterMemberLike {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  has_own_device: boolean;
  photos_visible: boolean;
}

export interface FocusCropStyle {
  objectFit: 'cover';
  objectPosition: string;
}

const CENTRE: FocusCropStyle = { objectFit: 'cover', objectPosition: '50% 50%' };

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/**
 * Where to point an `object-fit: cover` crop so the SUBJECT'S face survives it.
 *
 * `pfp_focus` is the subject's own box inside their profile picture, normalised
 * 0–1 from the top-left. `objectPosition` wants the point of interest, so the
 * centre of the box (`x + w/2`) is the answer — using `x` puts the face hard
 * against the frame edge.
 *
 * `null` centre-crops, and that is the ORDINARY case, not an error: a directly
 * uploaded picture has no box, and neither does one whose attribution has not
 * run yet. A non-finite box centre-crops too — a NaN in a CSS percentage
 * invalidates the declaration, so the crop would silently stop tracking
 * anything at all.
 */
export function focusCropStyle(focus: FaceBox | null): FocusCropStyle {
  if (focus === null) return CENTRE;
  const { x, y, w, h } = focus;
  if (![x, y, w, h].every((value) => Number.isFinite(value))) return CENTRE;
  const cx = clampPercent((x + w / 2) * 100);
  const cy = clampPercent((y + h / 2) * 100);
  return { objectFit: 'cover', objectPosition: `${String(cx)}% ${String(cy)}%` };
}

export function memberDisplayName(member: {
  first_name: string | null;
  last_name: string | null;
}): string {
  const parts = [member.first_name, member.last_name].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  // Never "null null". A member added seconds ago may have no name projected
  // yet, and the screen still has to address them.
  return parts.length === 0 ? 'this member' : parts.join(' ');
}

/** What fills the circle when `pfp_url` is null — which is the normal state. */
export function memberInitials(member: {
  first_name: string | null;
  last_name: string | null;
}): string {
  const letters = [member.first_name, member.last_name]
    .map((part) => part?.trim().charAt(0) ?? '')
    .filter((letter) => letter.length > 0)
    .join('')
    .toUpperCase();
  return letters.length === 0 ? '·' : letters;
}

/**
 * Whether a signed GET has run out. `null` means there is nothing to expire.
 *
 * AN UNPARSEABLE STAMP COUNTS AS EXPIRED. The cost of being wrong that way is
 * one roster refetch; the cost of the other way is a broken image with no
 * explanation, which reads as "the picture was lost".
 */
export function pfpExpired(expiresAt: string | null, now: number = Date.now()): boolean {
  if (expiresAt === null) return false;
  const stamp = new Date(expiresAt).getTime();
  if (Number.isNaN(stamp)) return true;
  return stamp <= now;
}

export interface NextCopy {
  title: string;
  detail: string;
  done: boolean;
}

/**
 * `next` in words. SERVER-OWNED — this maps the value to copy and decides
 * nothing about the order, which is the whole point of the field existing
 * (P23). `'photos'` is the value added by the per-member space; it sits AFTER
 * consent, because a photo of somebody becomes a search seed and seeding
 * before consent is signed is the ordering the enrolment's NOT NULL consent
 * columns exist to prevent.
 */
export function memberNextCopy(next: MemberState['next'], name: string): NextCopy {
  switch (next) {
    case 'email_verification':
      return {
        title: `${name} needs to verify their email`,
        detail:
          'A verification mail went out when they were added. Consent cannot be collected until it is confirmed.',
        done: false,
      };
    case 'quiz':
      return {
        title: `${name} has not answered their questions yet`,
        detail: 'The answers are recorded as theirs and set their own likeness health score.',
        done: false,
      };
    case 'consent':
      return {
        title: `${name}'s consent is not signed`,
        detail:
          'Their biometric consent has to be signed before their face signature can be created.',
        done: false,
      };
    case 'enrolment':
      return {
        title: `${name} has no face signature yet`,
        detail: 'The liveness capture creates it, and monitoring needs it.',
        done: false,
      };
    case 'photos':
      return {
        title: `${name} has no photos yet`,
        detail:
          'Add photos of them below. Their space and their allowance — nothing here counts against yours.',
        done: false,
      };
    case 'done':
      return { title: `${name} is set up`, detail: 'Nothing outstanding.', done: true };
  }
}

export interface LimitDetail {
  current: number;
  limit: number;
  remaining: number;
}

/**
 * The `409 LIMIT_EXCEEDED` block describes THAT SUBJECT'S allowance, so the
 * copy names them. Caching one person's remaining count and showing it for
 * another is the specific mistake the mobile contract calls out, and naming
 * the person is what makes it visible when it happens.
 */
export function photoLimitCopy(detail: LimitDetail | null, name: string): string {
  if (detail === null) return `${name}'s photo allowance is full.`;
  return `${name} has ${String(detail.current)} of ${String(detail.limit)} photos — ${String(detail.remaining)} left.`;
}

/**
 * The LIMIT_EXCEEDED payload, read off an error body without trusting its
 * shape. The api sends `{current, limit, remaining}`; a body that does not
 * carry them is still a real refusal and must render.
 */
export function limitDetailOf(body: unknown): LimitDetail | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  const { current, limit, remaining } = record;
  if (typeof current !== 'number' || typeof limit !== 'number' || typeof remaining !== 'number') {
    return null;
  }
  return { current, limit, remaining };
}

/**
 * IS THE FACE SCAN STILL OWED? — the one question `next` cannot answer.
 *
 * `nextStepFor` on the api never returns `'enrolment'`: reading that state
 * would need a cross-boundary read into `svc.v_person_enrolment_state` that
 * `memberState` does not have, and the value stays in the type only so adding
 * it later is not a breaking response change. So `next` steps from `consent`
 * straight to `'photos'`.
 *
 * Reading `'photos'` as "photos are all that is left" therefore tells a manager
 * nothing is outstanding for a member who has never been scanned — and a
 * member with no face signature is never monitored, which is the silent
 * failure the whole member loop exists to avoid.
 *
 * `enrolment_status` off the household roster is the missing half, so the two
 * are combined here, where a caller holds both. Only the steps AT OR AFTER
 * where enrolment sits can be owed it: an unanswered quiz is not "the scan is
 * owed", it is "the quiz is owed", and the loop must not jump the queue.
 */
export function faceScanOwed(
  next: MemberState['next'],
  enrolmentStatus: string,
): boolean {
  if (enrolmentStatus === 'active') return false;
  return next === 'photos' || next === 'done' || next === 'enrolment';
}
